import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../../../components/Header";
import LeftMenu from "../../../components/LeftMenu";
import EvaSlider from "../../eva/questionaire/components/EvaSlider";
import { functions } from "../../../constants/common-functions";
import { STRINGIFY } from "../../../constants/general-constants";

/* ====================================================== */
/*  COMPONENT                                             */
/* ====================================================== */

export default function WorkflowTask() {

  const { questionnaireResponseId } = useParams();
  const history = useHistory();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState({});
  const [overallComments, setOverallComments] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [animatedPercent, setAnimatedPercent] = useState(0);
  const [hasCelebrated, setHasCelebrated] = useState(false);
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);

  const animationRef = useRef(null);

  /* ====================================================== */
  /*  LOAD QUESTIONNAIRE                                    */
  /* ====================================================== */

  useEffect(function () {

    functions.getObjectFromAjax(
      "/resolutionsoftwareplatform/questionnaire/render/" + questionnaireResponseId,
      function (data) {
        setQuestionnaire(data);
      },
      null
    );

  }, [questionnaireResponseId]);

  /* ====================================================== */
  /*  RESPONSE HANDLERS                                     */
  /* ====================================================== */

  function handleScaleChange(questionId, value) {
    setResponses(function (prev) {
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
		  questionId:questionId,
          score: value
        }
      };
    });
  }

  function handleTextChange(questionId, text) {
    setResponses(function (prev) {
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
		  questionId:questionId,
          comment: text
        }
      };
    });
  }

  /* ====================================================== */
  /*  VALIDATION                                            */
  /* ====================================================== */

  function isValid() {
    if (!questionnaire) return false;

    for (let s = 0; s < questionnaire.sections.length; s++) {
      const section = questionnaire.sections[s];

      for (let q = 0; q < section.questions.length; q++) {
        const question = section.questions[q];
        const answer = responses[question.questionId];

        if (!answer || answer.score === null || answer.score === undefined) {
          alert("Please answer all questions before submitting.");
          return false;
        }
      }
    }
    return true;
  }

  /* ====================================================== */
  /*  SUBMIT                                                */
  /* ====================================================== */

  function submitQuestionnaire() {
    if (submitting) return;
    if (!isValid()) return;

    setSubmitting(true);

    const payload = {
      questionnaireResponseId: questionnaireResponseId,
      answers: Object.values(responses),
      comments: overallComments
    };

    functions.submitAjax(
      "/resolutionsoftwareplatform/questionnaire/submit",
      payload,
      handleSave,
      STRINGIFY,
      handleSubmitError
    );
  }

  function handleSave() {
    history.push("/home");
  }

  function handleSubmitError() {
    alert("There was a problem submitting the questionnaire.");
    setSubmitting(false);
  }

  /* ====================================================== */
  /*  PROGRESS HELPERS                                      */
  /* ====================================================== */

  function getSectionPercent(section) {
    const total = section.questions.length;
    if (total === 0) return 0;

    let answered = 0;

    section.questions.forEach(function (q) {
      const answer = responses[q.questionId];
      if (answer && answer.score !== null && answer.score !== undefined) {
        answered++;
      }
    });

    return Math.round((answered / total) * 100);
  }

  function getOverallProgressPercent() {
    if (!questionnaire) return 0;

    let total = 0;
    let answered = 0;

    questionnaire.sections.forEach(function (section) {
      section.questions.forEach(function (q) {
        total++;
        const answer = responses[q.questionId];
        if (answer && answer.score !== null && answer.score !== undefined) {
          answered++;
        }
      });
    });

    if (total === 0) return 0;
    return Math.round((answered / total) * 100);
  }

  /* ====================================================== */
  /*  ANIMATED PROGRESS + CONFETTI                          */
  /* ====================================================== */

  useEffect(function () {

    const target = getOverallProgressPercent();
    const start = animatedPercent;
    const duration = 500;
    const startTime = performance.now();

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    function step(timestamp) {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const value = Math.round(start + (target - start) * progress);
      setAnimatedPercent(value);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      }
    }

    animationRef.current = requestAnimationFrame(step);

    if (target === 100 && !hasCelebrated) {
      setHasCelebrated(true);

      const canvas = document.createElement("canvas");
      canvas.style.position = "fixed";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.width = "100vw";
      canvas.style.height = "100vh";
      canvas.style.pointerEvents = "none";
      canvas.style.zIndex = "9999";
      document.body.appendChild(canvas);

      const ctx = canvas.getContext("2d");
      const particles = [];

      for (let i = 0; i < 120; i++) {
        particles.push({
          x: window.innerWidth / 2,
          y: window.innerHeight * 0.6,
          vx: (Math.random() - 0.5) * 10,
          vy: Math.random() * -12,
          life: 60 + Math.random() * 30,
          color: ["#4DA8FF", "#F472B6", "#ffffff"][
            Math.floor(Math.random() * 3)
          ]
        });
      }

      function animate() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach(function (p) {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.4;
          p.life--;
          ctx.fillStyle = p.color;
          ctx.fillRect(p.x, p.y, 6, 6);
        });

        for (let i = particles.length - 1; i >= 0; i--) {
          if (particles[i].life <= 0) {
            particles.splice(i, 1);
          }
        }

        if (particles.length > 0) {
          requestAnimationFrame(animate);
        } else {
          document.body.removeChild(canvas);
        }
      }

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      animate();

      setTimeout(function () {
        setShowCelebrationModal(true);
      }, 1200);
    }

    return function () {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

  }, [responses]);

  /* ====================================================== */
  /*  STYLES                                                */
  /* ====================================================== */

  const COMPLETE_BUTTON = {
    padding: "12px 34px",
    background: "linear-gradient(180deg, #4DA8FF 0%, #1C72D4 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(28,114,212,0.35)"
  };

  /* ====================================================== */
  /*  RENDER                                                */
  /* ====================================================== */

  if (!questionnaire) {
    return <div style={{ padding: "40px" }}>Loading questionnaire...</div>;
  }

  return (
    <>
	{showCelebrationModal && (
	  <div
	    style={{
	      position: "fixed",
	      inset: 0,
	      background: "rgba(0,0,0,0.55)",
	      backdropFilter: "blur(4px)",
	      zIndex: 20000,
	      display: "flex",
	      alignItems: "center",
	      justifyContent: "center"
	    }}
	  >
	    <div
	      style={{
	        background: "linear-gradient(180deg, #ffffff 0%, #f6f9fc 100%)",
	        borderRadius: "22px",
	        padding: "40px 46px",
	        boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
	        textAlign: "center",
	        maxWidth: "420px",
	        width: "100%"
	      }}
	    >
	      <div style={{ fontSize: "36px", marginBottom: "10px" }}>🎉</div>

	      <div style={{ fontSize: "24px", fontWeight: "700", marginBottom: "12px" }}>
	        Survey Complete!
	      </div>

	      <div style={{ fontSize: "14px", color: "#555", marginBottom: "18px" }}>
	        Thanks for your feedback.  
	        Add any final comments below and submit your response.
	      </div>

	      <textarea
	        placeholder="Optional final comments..."
	        value={overallComments}
	        onChange={function (e) {
	          setOverallComments(e.target.value);
	        }}
	        style={{
	          width: "100%",
	          minHeight: "90px",
	          padding: "10px",
	          resize: "vertical",
	          marginBottom: "18px",
	          borderRadius: "10px",
	          border: "1px solid #ddd"
	        }}
	      />

	      <button
	        style={{
	          padding: "12px 34px",
	          background: "linear-gradient(180deg, #4DA8FF 0%, #1C72D4 100%)",
	          color: "#fff",
	          border: "none",
	          borderRadius: "999px",
	          fontSize: "14px",
	          fontWeight: "600",
	          cursor: submitting ? "not-allowed" : "pointer",
	          boxShadow: "0 6px 16px rgba(28,114,212,0.35)",
	          opacity: submitting ? 0.6 : 1
	        }}
	        onClick={submitQuestionnaire}
	        disabled={submitting}
	      >
	        {submitting ? "Submitting..." : "Submit Questionnaire"}
	      </button>
	    </div>
	  </div>
	)}

      <Header />
      <LeftMenu />

      <div style={{ display: "flex", marginLeft: "60px", marginTop: "60px" }}>

        {/* MAIN */}
        <div style={{ padding: "32px", width: "calc(100% - 330px)" }}>
          <h1>{questionnaire.title}</h1>
          <p>{questionnaire.description}</p>

          {questionnaire.sections.map(function (section) {
            return (
              <div key={section.id}>
                <h2>{section.title}</h2>

                {section.questions.map(function (q) {
                  return (
                    <div
                      key={q.questionId}
                      id={"eva-question-" + q.questionId}
                      style={{
                        borderRadius: "16px",
                        padding: "26px 32px 30px 32px",
                        marginBottom: "26px",
                        boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
                        background: "rgba(255,255,255,0.75)",
                        backdropFilter: "blur(6px)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        marginRight: "30px"
                      }}
                    >
                      <EvaSlider
                        question={q.questionText}
                        info={q.helpText}
                        onRelease={function (val) {
                          handleScaleChange(q.questionId, val);
                        }}
                        onTextChange={function (txt) {
                          handleTextChange(q.questionId, txt);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div style={{ height: "200px" }} />
        </div>

        {/* RIGHT PANEL */}
        <div
          style={{
            position: "fixed",
            top: "100px",
            right: "0px",
            width: "260px",
            height: "calc(100vh - 140px)",
            background: "linear-gradient(180deg, #2b2f35 0%, #1f2329 100%)",
            borderTopLeftRadius: "22px",
            borderBottomLeftRadius: "22px",
            boxShadow: "-10px 0 28px rgba(0,0,0,0.35)",
            padding: "22px 18px",
            display: "flex",
            flexDirection: "column",
            zIndex: 999
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "16px", position: "relative" }}>
            <svg width={120} height={120}>
              <circle stroke="rgba(255,255,255,0.15)" fill="transparent" strokeWidth={10} r={55} cx={60} cy={60} />
              <circle
                stroke="url(#evaGradient)"
                fill="transparent"
                strokeWidth={10}
                strokeDasharray={2 * Math.PI * 55}
                strokeDashoffset={(2 * Math.PI * 55) - (animatedPercent / 100) * (2 * Math.PI * 55)}
                strokeLinecap="round"
                r={55}
                cx={60}
                cy={60}
              />
              <defs>
                <linearGradient id="evaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#F472B6" />
                  <stop offset="100%" stopColor="#DB2777" />
                </linearGradient>
              </defs>
            </svg>

            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: animatedPercent === 100 ? "28px" : "22px",
              fontWeight: "700",
              color: "#ffffff"
            }}>
              {animatedPercent === 100 ? "😊" : animatedPercent + "%"}
            </div>

            <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "6px" }}>
              Complete
            </div>
          </div>

          {questionnaire.sections.map(function (section) {
            const percent = getSectionPercent(section);

            return (
              <div
                key={section.id}
                style={{
                  background: percent === 100
                    ? "linear-gradient(180deg, #4DA8FF 0%, #1C72D4 100%)"
                    : "linear-gradient(180deg, #3a3f46 0%, #2b2f35 100%)",
                  padding: "8px 14px",
                  borderRadius: "14px",
                  marginBottom: "10px",
                  color: "#fff",
                  cursor: "pointer"
                }}
                onClick={function () {
                  const first = section.questions[0];
                  if (first) {
                    const el = document.getElementById("eva-question-" + first.questionId);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }
                }}
              >
                {section.title} {percent}%
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
